import {
  Award,
  CheckCircle,
  Shield,
  MapPin,
  MessageCircle,
  Users,
  Calendar,
  Building2,
  Star,
} from "lucide-react";
import { Badge } from "@b2b/ui-components";
import { useNavigate, useLocation } from "react-router-dom";
import { formatFactorySizeForStorefront } from "../../../constants/factorySizeOptions";
import { MembershipPlanBadge } from "./MembershipPlanBadge";

type LooseObject = Record<string, unknown>;

const hasMeaningfulValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === "number") return Number.isFinite(value) && value > 0;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return false;
    const lower = trimmed.toLowerCase();
    return (
      lower !== "n/a" &&
      lower !== "na" &&
      lower !== "null" &&
      lower !== "undefined"
    );
  }
  return true;
};

const formatLocation = (raw?: string): string => {
  if (!raw) return "";
  const parts = raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.join(", ");
};

interface HeroBannerProps {
  companyName?: string;
  logoUrl?: string;
  location?: string;
  company?: LooseObject;
  businessInfo?: LooseObject;
  storefront?: LooseObject;
  sellerId?: string;
  userRole?: "admin" | "seller" | "buyer";
  showChatForAuthenticated?: boolean;
  embedded?: boolean;
  rating?: number | null;
  reviewCount?: number;
  membershipPlan?: string | null;
}

export const HeroBanner = ({
  companyName,
  logoUrl,
  location,
  company,
  businessInfo: businessInfoProp,
  storefront,
  sellerId,
  userRole,
  showChatForAuthenticated = false,
  embedded = false,
  rating = null,
  reviewCount = 0,
  membershipPlan = null,
}: HeroBannerProps) => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const businessInfo =
    (businessInfoProp as LooseObject) ||
    (storefront?.businessInfo as LooseObject) ||
    {};
  const employeeCount =
    businessInfo.employeeCount ?? company?.employeeCount ?? null;
  const rawFactorySize =
    businessInfo.factorySize ??
    company?.factorySize ??
    company?.totalFactorySize;
  const factorySizeDisplay = formatFactorySizeForStorefront(rawFactorySize);

  const experienceDisplay = (() => {
    const foundedYear = company?.foundedYear;
    if (foundedYear != null && foundedYear !== "") {
      const yearNum = Number(foundedYear);
      if (Number.isFinite(yearNum) && yearNum > 0) {
        const yrs = new Date().getFullYear() - yearNum;
        if (yrs > 0) return `${yrs}+ yrs`;
      }
    }
    const yearsInBusiness = businessInfo?.yearsInBusiness;
    if (hasMeaningfulValue(yearsInBusiness)) {
      return `${yearsInBusiness}+ yrs`;
    }
    return "";
  })();

  const certCount =
    (
      (company?.businessProfileExtended as LooseObject | undefined)
        ?.certificatesQuality as unknown[] | undefined
    )?.length ?? 0;

  const stats = [
    hasMeaningfulValue(employeeCount)
      ? {
          icon: Users,
          value: String(employeeCount),
          label: "Employees",
        }
      : null,
    experienceDisplay
      ? {
          icon: Calendar,
          value: experienceDisplay,
          label: "Experience",
        }
      : null,
    factorySizeDisplay
      ? {
          icon: Building2,
          value: factorySizeDisplay,
          label: "Factory Size",
        }
      : null,
    certCount > 0
      ? {
          icon: Award,
          value: String(certCount),
          label: "Certifications",
        }
      : null,
  ].filter(
    (stat): stat is { icon: typeof Users; value: string; label: string } =>
      stat !== null,
  );

  const isVerified = company?.approvalStatus === "approved";
  const displayCompanyName =
    typeof companyName === "string" && companyName.trim()
      ? companyName.trim()
      : "";
  const displayLocation = formatLocation(location);
  const hasQualityCerts =
    ((businessInfo?.qualityCertifications as unknown[] | undefined)?.length ??
      0) > 0;
  const hasMembershipPlan = Boolean(String(membershipPlan || "").trim());
  const showBadges = isVerified || hasQualityCerts || hasMembershipPlan;
  const hasStats = stats.length > 0;
  const hasRating = rating != null && Number(rating) > 0;
  const displayRating = hasRating ? Number(rating).toFixed(1) : null;

  const handleOpenChat = () => {
    const from = `${routeLocation.pathname}${routeLocation.search}`;
    if (sellerId) {
      navigate(`/chat?sellerId=${encodeURIComponent(sellerId)}`, {
        state: { from },
      });
      return;
    }
    navigate("/chat", { state: { from } });
  };

  const showChatButton = showChatForAuthenticated || userRole === "buyer";

  const shellClass = embedded
    ? "w-full border-b border-gray-100 px-4 py-5 sm:px-6 sm:py-6"
    : "relative border-b border-gray-200 bg-white";

  const innerWrapClass = embedded ? "" : "container mx-auto px-4 py-6 md:py-8";

  return (
    <div className={shellClass}>
      <div className={innerWrapClass}>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
          <div className="flex min-w-0 items-center gap-4 sm:gap-5 lg:min-w-[260px]">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm sm:h-24 sm:w-24">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={companyName}
                  className="h-full w-full object-cover"
                />
              ) : displayCompanyName ? (
                <span className="text-xl font-bold text-[#FF6A00]">
                  {displayCompanyName.substring(0, 2).toUpperCase()}
                </span>
              ) : (
                <Building2 className="h-8 w-8 text-[#FF6A00]/60" aria-hidden />
              )}
            </div>

            <div className="min-w-0">
              {displayCompanyName ? (
                <h1 className="truncate text-xl font-bold text-[#1A2A3D] sm:text-2xl">
                  {displayCompanyName}
                </h1>
              ) : null}

              {showBadges ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {hasMembershipPlan ? (
                    <MembershipPlanBadge plan={membershipPlan} />
                  ) : null}
                  {isVerified && (
                    <Badge className="border-0 bg-emerald-600 text-xs text-white">
                      <CheckCircle className="mr-1 h-3 w-3" />
                      Verified
                    </Badge>
                  )}
                  {hasQualityCerts && (
                    <Badge className="border-0 bg-blue-600 text-xs text-white">
                      <Shield className="mr-1 h-3 w-3" />
                      Certified
                    </Badge>
                  )}
                </div>
              ) : null}

              {displayLocation ? (
                <div className="mt-2 flex items-center gap-1 text-sm text-gray-500">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span className="truncate">{displayLocation}</span>
                </div>
              ) : null}
            </div>
          </div>

          {hasStats ? (
            <div
              className={`grid flex-1 gap-4 border-gray-100 pt-4 sm:grid-cols-2 lg:pt-0 ${
                stats.length >= 4
                  ? "grid-cols-2 sm:grid-cols-4"
                  : stats.length === 3
                    ? "grid-cols-2 sm:grid-cols-3"
                    : stats.length === 2
                      ? "grid-cols-2"
                      : "grid-cols-1"
              } border-t lg:border-l lg:border-t-0 lg:pl-6`}
            >
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="flex flex-col gap-1">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Icon className="h-4 w-4 text-[#FF6A00]" />
                      {stat.label}
                    </div>
                    <div className="text-base font-semibold text-[#1A2A3D] sm:text-lg">
                      {stat.value}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {hasRating || showChatButton ? (
            <div className="flex flex-col items-stretch gap-3 sm:items-center lg:min-w-[180px] lg:items-end">
              {hasRating && (
                <div className="flex items-center justify-center gap-2 lg:justify-end">
                  <div className="flex items-center gap-0.5">
                    <Star className="h-4 w-4 fill-[#FF6A00] text-[#FF6A00]" />
                    <span className="text-lg font-bold text-[#1A2A3D]">
                      {displayRating}
                    </span>
                  </div>
                  {reviewCount > 0 && (
                    <span className="text-xs text-gray-500">
                      ({reviewCount} {reviewCount === 1 ? "review" : "reviews"})
                    </span>
                  )}
                </div>
              )}

              {showChatButton && (
                <button
                  type="button"
                  onClick={handleOpenChat}
                  className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md border border-[#FF6A00] bg-white px-5 py-2.5 text-sm font-semibold text-[#FF6A00] transition-colors hover:bg-[#FF6A00] hover:text-white sm:w-auto"
                  aria-label="Open chat with supplier"
                >
                  <MessageCircle className="h-4 w-4" />
                  {showChatForAuthenticated ? "Chat Now" : "Chat with supplier"}
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
